import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Truck, Search, ChevronDown } from 'lucide-react'
import { shippingService } from '@/services/shippingService'
import { usePagination } from '@/hooks/usePagination'
import { useDebounce } from '@/hooks/useDebounce'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import { formatDateTime, getLocalizedField } from '@/utils/formatters'

const STATUS_META = {
  pending:     { label: 'قيد الانتظار',   color: '#767683', bg: 'rgba(118,118,131,0.08)' },
  processing:  { label: 'جارٍ التجهيز',   color: '#FF6D00', bg: 'rgba(255,109,0,0.08)' },
  in_transit:  { label: 'في الطريق',      color: '#0077B6', bg: 'rgba(0,119,182,0.08)' },
  delivered:   { label: 'تم التسليم',     color: '#1B6D24', bg: 'rgba(27,109,36,0.08)' },
  returned:    { label: 'مُعاد',           color: '#BA1A1A', bg: 'rgba(186,26,26,0.08)' },
}

export default function ShipmentsPage() {
  const { page, limit, setPage } = usePagination(1, 20)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const debouncedSearch = useDebounce(search, 400)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['shipments-admin', { search: debouncedSearch, status: statusFilter, page, limit }],
    queryFn: () => shippingService.getShipments({
      search: debouncedSearch || undefined,
      status: statusFilter || undefined,
      page,
      limit,
    }),
    retry: 1,
  })

  const shipments = data?.data || []
  const meta      = data?.meta || {}

  return (
    <div className="space-y-5">

      <div>
        <h1 className="font-black text-navy text-xl" style={{ fontFamily: 'Cairo, sans-serif' }}>الشحنات</h1>
        <p className="text-sm text-ghost mt-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>متابعة جميع الشحنات عبر المنصة</p>
      </div>

      {/* Filters */}
      <div className="hg-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-52">
          <Search size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
          <input className="hg-input" style={{ paddingRight: 34 }}
            placeholder="ابحث برقم التتبع أو الطلب..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
        </div>
        <div className="relative">
          <select className="hg-input cursor-pointer min-w-36" style={{ paddingLeft: 28 }}
            value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}>
            <option value="">كل الحالات</option>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
        </div>
        {meta.total != null && (
          <span className="text-xs font-bold px-3 py-1.5 rounded-xl" style={{ background: 'rgba(0,6,102,0.06)', color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
            {meta.total} شحنة
          </span>
        )}
      </div>

      {/* Table */}
      <div className="hg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p style={{ color: '#BA1A1A', fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>فشل تحميل الشحنات</p>
            <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button>
          </div>
        ) : shipments.length === 0 ? (
          <EmptyState icon={Truck} message="لا توجد شحنات بهذه المعايير" />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9F9F9', borderBottom: '2px solid #F3F3F3' }}>
                    {['رقم التتبع', 'شركة الشحن', 'رقم الطلب', 'البائع', 'التسليم المتوقع', 'الحالة', 'التاريخ'].map((h) => (
                      <th key={h} style={{ textAlign: 'right', padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#767683', whiteSpace: 'nowrap', fontFamily: 'Tajawal, sans-serif' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s, idx) => {
                    const sm = STATUS_META[s.status] || STATUS_META.pending
                    return (
                      <tr key={s.id} style={{ borderBottom: idx === shipments.length - 1 ? 'none' : '1px solid #F3F3F3' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#F9F9F9'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 14px' }}>
                          <span dir="ltr" style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#000666', background: 'rgba(0,6,102,0.06)', padding: '2px 8px', borderRadius: 6 }}>
                            {s.trackingNumber || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#1A1C1C', fontWeight: 600 }}>{s.carrier || '—'}</span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12, color: '#454652' }}>
                            {s.orderItem?.order?.orderNumber || s.orderNumber || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#454652' }}>
                            {getLocalizedField(s.vendor?.storeName, 'ar') || s.vendor?.storeName || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: s.estimatedDelivery ? '#1A1C1C' : '#C4C4C4' }}>
                            {s.estimatedDelivery || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sm.bg, color: sm.color, fontFamily: 'Tajawal, sans-serif', whiteSpace: 'nowrap' }}>
                            {sm.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>
                            {formatDateTime(s.createdAt)}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '0 16px 16px' }}>
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
